import { useState, useEffect, useRef } from "react";
import clsx from "clsx";
import axios from "../../../api/axios";
import {
  BarChart,
  ResponsiveContainer,
  Bar,
  Tooltip,
  YAxis,
  XAxis,
  LabelList,
} from "recharts";
import useConfig from "../../../hooks/useConfig";
import useAuth from "../../../hooks/authenticate/useAuth";
import useCsvInput from "../../../hooks/form/useCsvInput";

function getLocalHour(date) {
  if (date) {
    let day = new Date(date).getDate();
    let month = new Date(date).getMonth() + 1;
    return `${day}.${month}`;
  }
}

function mergeSameDays(timeline) {
  let data = [];
  let days = new Set();
  for (let i = 0; i < timeline.length; i++) {
    if (i === 0) {
      days.add(timeline[i]["date"]);
      let currentChecker = timeline[i];
      for (let j = 1; j < timeline.length; j++) {
        if (currentChecker["date"] === timeline[j]["date"]) {
          currentChecker["inCount"] += timeline[j]["inCount"];
          currentChecker["outCount"] += timeline[j]["outCount"];
          currentChecker["total"] += timeline[j]["total"];
        }
      }
      data.push(currentChecker);
    } else if (!days.has(timeline[i]["date"])) {
      days.add(timeline[i]["date"]);
      let currentChecker = timeline[i];
      for (let j = i + 1; j < timeline.length; j++) {
        if (currentChecker["date"] === timeline[j]["date"]) {
          currentChecker["inCount"] += timeline[j]["inCount"];
          currentChecker["outCount"] += timeline[j]["outCount"];
          currentChecker["total"] += timeline[j]["total"];
        }
      }
      data.push(currentChecker);
    } else continue;
  }
  return data.sort(
    (a, b) =>
      new Date(
        new Date().getFullYear(),
        parseInt(a["date"].split(".")[1]),
        parseInt(a["date"].split(".")[0])
      ) -
      new Date(
        new Date().getFullYear(),
        parseInt(b["date"].split(".")[1]),
        parseInt(b["date"].split(".")[0])
      )
  );
}
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    if (
      payload[0] &&
      payload[1] &&
      payload[0].value === 0 &&
      payload[1].value === 0
    ) {
      return null;
    } else if (payload[0] && payload[0].value === 0) {
      return null;
    } else if (payload[1] && payload[1].value === 0) {
      return null;
    }
    return (
      <div className="bg-white shadow-xl p-4 text-sm border rounded-md space-y-1 animate-fadeIn">
        {payload[0] && (
          <p
            className={clsx({
              "text-blue-500 text-opacity-80": payload[0].dataKey === "inCount",
            })}
          >{`Entering : ${payload[0].value.toLocaleString()}`}</p>
        )}
        {payload[1] && (
          <p
            className={clsx({
              "text-indigo-500 text-opacity-80":
                payload[1].dataKey === "outCount",
            })}
          >{`Leaving : ${payload[1].value.toLocaleString()}`}</p>
        )}
        {/* <p className="text-gray-400">Total: {totalVisits.toLocaleString()}</p> */}
      </div>
    );
  }

  return null;
};

const CreativeReport = () => {
  const { config } = useConfig();
  const { auth } = useAuth();
  const [timeline, setTimeline] = useState([{ date: "", objects: 0 }]);
  const [devices, setDevices] = useState([]);
  const [selectToHour, setSelectToHour] = useState("23:59");
  const [selectFromHour, setSelectFromHour] = useState("00:00");
  const [selectToHourMenu, setSelectToHourMenu] = useState([]);
  const [selectFromHourMenu, setSelectFromHourMenu] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const [isValid, setIsValid] = useState(true);
  const errRef = useRef();
  const [fromDate, setFromDate] = useState(
    new Date(
      new Date().getFullYear(),
      new Date().getMonth(),
      new Date().getDate() + 1
    )
      .toISOString()
      .split("T")[0]
  );
  const [daysRange, setDaysRange] = useState(
    new Date(new Date().getFullYear(), new Date().getMonth(), 0).getDate()
  );
  const [toDate, setToDate] = useState(
    new Date(
      new Date().getFullYear(),
      new Date().getMonth(),
      new Date().getDate() + 1
    )
      .toISOString()
      .split("T")[0]
  );
  const [dispatch, csvInput] = useCsvInput();
  const [isSubmit, setSubmit] = useState(false);
  const [errMsg, setErrMsg] = useState("");

  function getDifferenceInDays(date1, date2) {
    const diffInMs = Math.abs(date2 - date1);
    return diffInMs / (1000 * 60 * 60 * 24);
  }
  function initDevices() {
    let devices = new Map();
    config.areas[3]["devices"].forEach((device, index) => {
      devices.set(device.deviceId, { azimuth: device.azimuth, index: index });
    });
    setDevices((prev) => devices);
  }
  function initHours() {
    let toHourMenu = [];
    let fromHourMenu = [];
    for (let i = 0; i < 24; i++) {
      if (i < 10) {
        toHourMenu.push(`0${i}:59`);
        fromHourMenu.push(`0${i}:00`);
      } else {
        toHourMenu.push(`${i}:59`);
        fromHourMenu.push(`${i}:00`);
      }
    }
    setSelectToHourMenu((prev) => toHourMenu);
    setSelectFromHourMenu((prev) => fromHourMenu);
  }
  useEffect(() => {
    initDevices();
    initHours();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmitForm = (event) => {
    event.preventDefault();

    let days = getDifferenceInDays(new Date(fromDate), new Date(toDate));
    setDaysRange((prev) => days);
    let fromMonth = parseInt(fromDate.split("-")[1]);
    let fromDay = parseInt(fromDate.split("-")[2]);
    let toMonth = parseInt(toDate.split("-")[1]);
    let toDay = parseInt(toDate.split("-")[2]);
    setIsLoading((prev) => true);
    !isSubmit && setSubmit(true);
    axios
      .get(
        `v1/reports?token=${auth.token}&fromMonth=${fromMonth}&fromDay=${fromDay}
      &toMonth=${toMonth}&toDay=${toDay}&days=${daysRange}&fromHour=${selectFromHour}&&toHour=${selectToHour}`
        // {
        //   withCredentials: true,
        // }
      )
      .then((res) => {
        let timeline = [];
        let data = res.data.filter((value) => value.length > 0);
        for (let i = 0; i < data.length; i++) {
          for (let j = 0; j < data[i].length; j++) {
            if (devices.has(data[i][j]["sensor"])) {
              if (res.data[i][j]) {
                let inCount = 0;
                let outCount = 0;
                let toNorthEastCount =
                  res.data[i][j]["dimXY"].length > 0
                    ? res.data[i][j]["dimXY"]
                        .map((val) => {
                          return val["directions"].filter((val) => {
                            return val === "toNorthEast";
                          }).length;
                        })
                        .reduce((a, b) => a + b)
                    : 0;

                let toNorthWestCount =
                  res.data[i][j]["dimXY"].length > 0
                    ? res.data[i][j]["dimXY"]
                        .map((val) => {
                          return val["directions"].filter((val) => {
                            return val === "toNorthWest";
                          }).length;
                        })
                        .reduce((a, b) => a + b)
                    : 0;
                let toSouthEastCount =
                  res.data[i][j]["dimXY"].length > 0
                    ? res.data[i][j]["dimXY"]
                        .map((val) => {
                          return val["directions"].filter((val) => {
                            return val === "toSouthEast";
                          }).length;
                        })
                        .reduce((a, b) => a + b)
                    : 0;
                let toSouthWestCount =
                  res.data[i][j]["dimXY"].length > 0
                    ? res.data[i][j]["dimXY"]
                        .map((val) => {
                          return val["directions"].filter((val) => {
                            return val === "toSouthWest";
                          }).length;
                        })
                        .reduce((a, b) => a + b)
                    : 0;

                let device = devices.get(data[i][j]["sensor"]);
                if (device.azimuth < 180) {
                  if (device.azimuth > 135) {
                    if (device.index === 1) {
                      //device 2
                      inCount = toNorthEastCount + toNorthWestCount;
                      outCount = toSouthEastCount + toSouthWestCount;
                    } else if (device.index === 4) {
                      //device 5
                      inCount = toSouthEastCount + toNorthEastCount;
                      outCount = toSouthWestCount + toNorthWestCount;
                    }
                  } else {
                    //device 3
                    inCount = toSouthEastCount + toSouthWestCount;
                    outCount = toNorthEastCount + toNorthWestCount;
                  }
                } else {
                  if (device.azimuth > 315) {
                    //device 1
                    inCount = toNorthEastCount + toNorthWestCount;
                    outCount = toSouthEastCount + toSouthWestCount;
                  } else {
                    if (device.index === 3) {
                      //device 4
                      inCount = toNorthEastCount + toSouthEastCount;
                      outCount = toNorthWestCount + toSouthWestCount;
                    } else if (device.index === 5) {
                      //device 6
                      inCount = toNorthWestCount + toSouthWestCount;
                      outCount = toNorthEastCount + toSouthEastCount;
                    }
                  }
                }

                let total = inCount > outCount ? inCount : outCount;

                timeline.push({
                  date: getLocalHour(data[i][j]["date"]),
                  inCount: inCount !== undefined ? inCount : 0,
                  outCount: outCount !== undefined ? outCount : 0,
                  total: total !== undefined ? total : 0,
                });
              }
            }
            // timeline.push({
            //   date: getLocalHour(data[i][j]["date"]),
            //   objects: parseInt(data[i][j]["objectsCount"]),
            // });
          }
        }
        if (timeline.length > 0) {
          timeline = mergeSameDays(timeline);
          dispatch({
            type: "SET_CSV",
            csv: {
              headers: [
                {
                  label: "Date",
                  key: "date",
                },
                {
                  label: "Entering",
                  key: "inCount",
                },
                {
                  label: "Leaving",
                  key: "outCount",
                },
              ],
              data: timeline.map((val) => {
                return {
                  date: val.date,
                  inCount: val.inCount,
                  outCount: val.outCount,
                };
              }),
              desc: `hours-between-${selectFromHour}-${selectToHour}`,
            },
          });
          setTimeout(() => {
            setTimeline((prev) => timeline);
            setIsLoading((prev) => false);
          }, 5000);
        } else {
          setTimeline((prev) => timeline);
          setIsLoading((prev) => false);
        }
      });
  };

  if (!isSubmit)
    return (
      <div className=" bg-white rounded-md w-full h-full  flex flex-col shadow-sm ">
        <div className="w-full p-1 flex flex-col">
          <form
            onSubmit={onSubmitForm}
            className=" h-full flex flex-row items-center justify-center border-b-2 shadow-sm"
          >
            <div className=" flex">
              <label className="flex flex-row  items-center text-xs font-extrabold pl-3">
                <div className="pr-1">From:</div>
                <input
                  className="pr-2 py-1 rounded-md"
                  type={"date"}
                  value={fromDate}
                  required
                  onChange={(e) => setFromDate(e.target.value)}
                  max={
                    new Date(
                      new Date().getFullYear(),
                      new Date().getMonth(),
                      new Date().getDate() + 1
                    )
                      .toISOString()
                      .split("T")[0]
                  }
                />
              </label>
              <label className=" flex flex-row items-center text-xs font-extrabold pl-3">
                <div className="pr-1">To:</div>
                <input
                  className="px-2 py-1 rounded-md"
                  type={"date"}
                  value={toDate}
                  onChange={(e) => {
                    setToDate(e.target.value);
                  }}
                  required
                  max={
                    new Date(
                      new Date().getFullYear(),
                      new Date().getMonth(),
                      new Date().getDate() + 1
                    )
                      .toISOString()
                      .split("T")[0]
                  }
                />
              </label>
            </div>

            <div className="flex flex-row  justify-center">
              <div className=" text-xs font-extrabold  rounded-md ">
                <div className="flex flex-row justify-center items-center">
                  <div className="pr-2">From:</div>
                  <select
                    id="From"
                    className="pr-2 py-1 rounded-md"
                    value={selectFromHour}
                    onChange={(e) => {
                      setSelectFromHour(e.target.value);
                    }}
                  >
                    {selectFromHourMenu.map((opt, index) => (
                      <option id={index} value={opt} key={index}>
                        {opt}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="text-xs  font-extrabold  rounded-md ">
                <div className="flex flex-row justify-center items-center">
                  <div className="px-2">To:</div>
                  <select
                    id="To"
                    className="pr-2 py-1 rounded-md bg-white"
                    value={selectToHour}
                    onChange={(e) => setSelectToHour(e.target.value)}
                  >
                    {selectToHourMenu.map((opt, index) => (
                      <option
                        className="bg-white focus-within:border-white"
                        id={index}
                        key={index}
                        value={opt}
                      >
                        {opt}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="flex items-center">
              <input
                className={
                  isValid
                    ? `bg-white text-sm hover:bg-gray-200 hover:rounded-xl  rounded-md px-2`
                    : "opacity-50 bg-white text-sm hover:bg-gray-200 hover:rounded-xl  rounded-md px-2 "
                }
                type={"submit"}
                disabled={!isValid}
              />
            </div>
          </form>
          <p
            ref={errRef}
            className={
              !isValid ? ` text-red-600  text-xs visible ` : "hidden invisible "
            }
          >
            {errMsg}
          </p>
        </div>
        <div className="w-full h-full  bg-gray-200 flex items-center rounded-lg justify-center mt-2">
          <p className="self-center  bg-white text-center text-xl rounded-xl">
            Please Choose a Date
          </p>
        </div>
      </div>
    );
  return (
    <div className="pt-1 px-1 bg-white rounded-md w-full h-full flex flex-col  ">
      <div className="w-full  flex flex-col ">
        <form
          onSubmit={onSubmitForm}
          className="  flex flex-row items-center justify-center border-b-2 shadow-sm"
        >
          <div className="flex ">
            <label className="flex flex-row  items-center text-xs font-extrabold pl-2">
              <div className="pr-1">From:</div>
              <input
                className="pr-2 py-1 rounded-md"
                type={"date"}
                value={fromDate}
                required
                onChange={(e) => setFromDate(e.target.value)}
                max={
                  new Date(
                    new Date().getFullYear(),
                    new Date().getMonth(),
                    new Date().getDate() + 1
                  )
                    .toISOString()
                    .split("T")[0]
                }
              />
            </label>
            <label className=" flex flex-row items-center text-xs font-extrabold pl-2">
              <div className="pr-1">To:</div>
              <input
                className="px-2 py-1 rounded-md"
                type={"date"}
                value={toDate}
                onChange={(e) => {
                  setToDate(e.target.value);
                }}
                required
                max={
                  new Date(
                    new Date().getFullYear(),
                    new Date().getMonth(),
                    new Date().getDate() + 1
                  )
                    .toISOString()
                    .split("T")[0]
                }
              />
            </label>
          </div>

          <div className="  flex flex-row  justify-center">
            <div className=" text-xs font-extrabold  rounded-md ">
              <div className="flex flex-row justify-center items-center">
                <div className="pr-2">From:</div>
                <select
                  id="From"
                  className="pr-2 py-1 rounded-md"
                  value={selectFromHour}
                  onChange={(e) => {
                    setSelectFromHour(e.target.value);
                  }}
                >
                  {selectFromHourMenu.map((opt, index) => (
                    <option id={index} value={opt} key={index}>
                      {opt}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="text-xs  font-extrabold  rounded-md ">
              <div className="flex flex-row justify-center items-center">
                <div className="px-2">To:</div>
                <select
                  id="To"
                  className="pr-2 py-1 rounded-md bg-white"
                  value={selectToHour}
                  onChange={(e) => setSelectToHour(e.target.value)}
                >
                  {selectToHourMenu.map((opt, index) => (
                    <option
                      className="bg-white focus-within:border-white"
                      id={index}
                      key={index}
                      value={opt}
                    >
                      {opt}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="flex items-center">
            <input
              className={
                isValid
                  ? `bg-white text-sm hover:bg-gray-200 hover:rounded-xl  rounded-md px-2`
                  : "opacity-50 bg-white text-sm hover:bg-gray-200 hover:rounded-xl  rounded-md px-2 "
              }
              type={"submit"}
              disabled={!isValid}
            />
          </div>
          <div className="px-1">
            <>{csvInput}</>
          </div>
        </form>
        <p
          ref={errRef}
          className={
            !isValid ? ` text-red-600  text-xs visible ` : "hidden invisible "
          }
        >
          {errMsg}
        </p>
      </div>
      {isLoading && (
        <div className="w-full h-full contents bg-white ">
          <div className="bg-gray-200 w-full h-full  rounded-md overflow-hidden transition-all">
            <div className="w-full h-full opacity-50 transform -translate-x-full bg-gradient-to-r from-transparent via-white to-transparent  animate-slide"></div>
          </div>
        </div>
      )}
      {!isLoading && (
        <div className={`w-full h-95/100`}>
          <div className="w-full  h-95/100 mt-2 ">
            <ResponsiveContainer>
              <BarChart
                data={timeline}
                margin={{ top: 20, right: 2, left: 20, bottom: 5 }}
                barGap="8"
                // height="50%"
              >
                <defs>
                  <linearGradient id="inCount" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="50%" stopColor="#0054c9" opacity={0.5} />
                    <stop offset="95%" stopColor="#8884d8" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <defs>
                  <linearGradient id="outCount" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="50%" stopColor="#7206c4" opacity={0.5} />
                    <stop offset="95%" stopColor="#8884d8" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <Tooltip
                  coordinate={{ x: 0, y: 0 }}
                  offset={-50}
                  allowEscapeViewBox={{ x: true, y: true }}
                  content={<CustomTooltip />}
                  formatter={(Total) =>
                    new Intl.NumberFormat("en").format(Total)
                  }
                  // formatter={<customFormatter />}
                  cursor={false}
                />
                <XAxis
                  axisLine={false}
                  tick={{ fontSize: "0.7rem" }}
                  interval="preserveStartEnd"
                  height={15}
                  dataKey="date"
                  tickFormatter={(value) =>
                    new Date(
                      new Date().getFullYear(),
                      parseInt(value.split(".")[1]) - 1,
                      parseInt(value.split(".")[0])
                    )
                      .toDateString()
                      .slice(0, 4) +
                    " " +
                    new Intl.NumberFormat("en").format(value)
                  }
                />
                <YAxis
                  axisLine={false}
                  tickLine={true}
                  tick={{
                    fontSize: "0.7rem",
                  }}
                  width={30}
                  interval="preserveStartEnd"
                  dataKey="total"
                  tickFormatter={(value) =>
                    new Intl.NumberFormat("en").format(value)
                  }
                />
                {
                  <Bar dataKey="inCount" fill="url(#inCount)" fillOpacity={0.7}>
                    <LabelList
                      dataKey="inCount"
                      fill="#0054c9"
                      objects
                      fontSize={"0.65rem"}
                      position={"top"}
                      formatter={(value) =>
                        "Entering " + new Intl.NumberFormat("en").format(value)
                      }
                    ></LabelList>
                  </Bar>
                }
                {
                  <Bar
                    dataKey="outCount"
                    fill="url(#outCount)"
                    fillOpacity={0.7}
                  >
                    <LabelList
                      dataKey="outCount"
                      fill="#7206c4"
                      objects
                      fontSize={"0.65rem"}
                      position={"top"}
                      formatter={(value) =>
                        "Leaving " + new Intl.NumberFormat("en").format(value)
                      }
                    ></LabelList>
                  </Bar>
                }
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreativeReport;
